package com.eladfinish.bible.contest.extensions

// %[argument_index$][flags][width][.precision]conversion — the part of java.util.Formatter's
// syntax the app's format strings use.
private val formatSpecifier = Regex("""%(?:(\d+)\$)?([-0+ ,]*)(\d+)?(?:\.(\d+))?([sSdfn%])""")

/**
 * A subset of `java.util.Formatter`, which Kotlin/JS doesn't have: the conversions `%s`, `%S`,
 * `%d`, `%f`, `%n` and `%%`, with an explicit argument index (`%2$s`), a width, a precision, and
 * the flags `-` (left-justify), `0` (zero-pad) and `+` (always show the sign). A specifier whose
 * argument is missing is left in the text as written.
 */
actual fun String.formatString(vararg args: Any?): String {
    var nextOrdinaryIndex = 0
    return formatSpecifier.replace(this) { match ->
        val (explicitIndex, flags, widthText, precisionText, conversion) = match.destructured

        when (conversion) {
            "%" -> return@replace "%"
            "n" -> return@replace "\n"
        }

        val index = explicitIndex.toIntOrNull()?.minus(1) ?: nextOrdinaryIndex++
        if (index !in args.indices) return@replace match.value

        val precision = precisionText.toIntOrNull()
        val arg = args[index]
        val body = when (conversion) {
            "d" -> formatInteger(arg, forceSign = '+' in flags)
            "f" -> formatDecimal(arg, precision = precision ?: 6, forceSign = '+' in flags)
            "S" -> arg.toString().uppercase().truncate(precision)
            else -> arg.toString().truncate(precision)
        }

        body.pad(width = widthText.toIntOrNull(), leftJustify = '-' in flags, zeroPad = '0' in flags)
    }
}

private fun String.truncate(precision: Int?): String = if (precision != null) take(precision) else this

private fun formatInteger(arg: Any?, forceSign: Boolean): String {
    val number = (arg as? Number)?.toLong() ?: return arg.toString()
    return if (forceSign && number >= 0) "+$number" else number.toString()
}

private fun formatDecimal(arg: Any?, precision: Int, forceSign: Boolean): String {
    val number = (arg as? Number)?.toDouble() ?: return arg.toString()
    val fixed = number.asDynamic().toFixed(precision).unsafeCast<String>()
    return if (forceSign && number >= 0) "+$fixed" else fixed
}

private fun String.pad(width: Int?, leftJustify: Boolean, zeroPad: Boolean): String = when {
    width == null || length >= width -> this
    leftJustify -> padEnd(width)
    zeroPad && (startsWith('-') || startsWith('+')) -> first() + drop(1).padStart(width - 1, '0')
    zeroPad -> padStart(width, '0')
    else -> padStart(width)
}
